import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Database, Eye, Trash2, Bot, CheckCircle, XCircle, Scale } from 'lucide-react';
import { IMAGES, bgStyle } from '@/config/images';

const Section = ({ icon: Icon, iconColor = 'text-orange-500', title, children }) => (
  <div className="bg-black/70 backdrop-blur-sm border border-zinc-800 rounded-lg p-8">
    <div className="flex items-center gap-3 mb-6">
      <Icon className={`w-6 h-6 ${iconColor}`} />
      <h2 className="text-xl font-bold text-white">{title}</h2>
    </div>
    <div className="prose prose-invert max-w-none text-zinc-300 space-y-3">
      {children}
    </div>
  </div>
);

const PrivacyPage = () => {
  const lastUpdated = 'April 2026';

  return (
    <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" style={bgStyle(IMAGES.TERMS_PRIVACY)}>
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
      <div className="max-w-4xl mx-auto">

        {/* Back Link */}
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-orange-500/20 rounded-lg">
              <Shield className="w-8 h-8 text-orange-500" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-1">LEGAL</div>
              <h1
                className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Privacy Policy
              </h1>
              <p className="text-sm text-zinc-500 mt-1">
                Last updated: {lastUpdated} &nbsp;·&nbsp; CNC-Helpdesk (Andre Booysen)
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">

          {/* 1. What We Collect */}
          <Section icon={Database} iconColor="text-cyan-400" title="1. What We Collect">
            <p>CNC-Helpdesk collects only the minimum data needed to operate the service:</p>
            <div className="overflow-x-auto mt-2">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-zinc-700">
                    <th className="py-2 pr-4 text-zinc-400 font-medium">Data</th>
                    <th className="py-2 pr-4 text-zinc-400 font-medium">Why</th>
                    <th className="py-2 text-zinc-400 font-medium">How stored</th>
                  </tr>
                </thead>
                <tbody className="text-zinc-300">
                  {[
                    ['Email address', 'Account login and identification', 'Encrypted at rest'],
                    ['Display name', 'Community and marketplace visibility', 'Plain text'],
                    ['Password', 'Authentication only', 'Bcrypt hash — never readable'],
                    ['Machine profiles & settings', 'Personalised recommendations', 'Encrypted at rest'],
                    ['Marketplace listings', 'Feature functionality', 'Plain text'],
                    ['Community posts & messages', 'Feature functionality', 'Plain text'],
                    ['Subscription status', 'Access control', 'Encrypted at rest'],
                    ['Feedback submissions', 'Platform improvement', 'Encrypted at rest'],
                    ['Payment info', 'Not stored by us', 'Handled by Stripe'],
                  ].map(([data, why, how], i) => (
                    <tr key={i} className="border-b border-zinc-800/60">
                      <td className="py-2 pr-4 font-medium text-white">{data}</td>
                      <td className="py-2 pr-4">{why}</td>
                      <td className="py-2 text-zinc-400 text-xs">{how}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* 2. Where Data Is Stored */}
          <Section icon={Database} iconColor="text-orange-400" title="2. Where Your Data Is Stored">
            <p>
              All user data is stored in <strong className="text-white">MongoDB Atlas</strong>, a cloud database
              managed by MongoDB, Inc.
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                Where available, CNC-Helpdesk uses the{' '}
                <strong className="text-white">South Africa (Johannesburg)</strong> region to keep your data
                geographically close to local users and to support compliance with South African data laws.
              </li>
              <li>MongoDB Atlas encrypts all data at rest and in transit using industry-standard protocols.</li>
              <li>
                MongoDB's own privacy and security policies apply to data hosted on their infrastructure.
                See <a href="https://www.mongodb.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:underline">mongodb.com/legal/privacy-policy</a>.
              </li>
            </ul>
          </Section>

          {/* 3. AI and Third-Party Services */}
          <Section icon={Bot} iconColor="text-blue-400" title="3. AI & Third-Party Services">
            <p>CNC-Helpdesk uses the following third-party services:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="text-white">Anthropic (Claude Sonnet)</strong> — powers the AI assistant.
                Queries you send to the AI assistant are processed by Anthropic's API. Anthropic's{' '}
                <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:underline">privacy policy</a>{' '}
                governs their use of that data. CNC-Helpdesk does not log or store your AI conversation history beyond the current session.
              </li>
              <li>
                <strong className="text-white">Stripe</strong> — handles all payment processing. CNC-Helpdesk
                never sees or stores your credit card or bank details.
              </li>
              <li>
                <strong className="text-white">Resend</strong> — used for transactional email (account
                verification, password resets, notifications).
              </li>
              <li>
                <strong className="text-white">Vercel</strong> — hosts the frontend application.
              </li>
              <li>
                <strong className="text-white">Railway</strong> — hosts the backend API.
              </li>
            </ul>
          </Section>

          {/* 4. What We Don't Do */}
          <Section icon={Eye} iconColor="text-green-400" title="4. What We Do NOT Do">
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                'Sell your data to any third party',
                'Use your data for advertising',
                'Track you across other websites',
                'Store your payment card details',
                'Analyse your project data for commercial gain',
                'Share your machine profiles or settings externally',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <XCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  <span className="text-sm">{item}</span>
                </div>
              ))}
            </div>
          </Section>

          {/* 5. Your Rights */}
          <Section icon={Trash2} iconColor="text-yellow-400" title="5. Your Rights (POPIA & General)">
            <p>
              The Protection of Personal Information Act (POPIA) and general privacy principles give you the
              following rights regarding your data:
            </p>
            <ul className="space-y-2">
              {[
                ['Access', 'Request a summary of what data we hold about you.'],
                ['Correct', 'Update your display name, email, or settings in your Profile at any time.'],
                ['Delete', 'Permanently delete your account and all associated data from your Profile page. Deletion is processed within 30 days.'],
                ['Export', 'Request a copy of your data by contacting us via the feedback button or email.'],
                ['Object', 'Object to any processing of your data by contacting us directly.'],
              ].map(([right, desc], i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                  <span><strong className="text-white">{right}:</strong> {desc}</span>
                </li>
              ))}
            </ul>
          </Section>

          {/* 6. Data Retention */}
          <Section icon={Database} iconColor="text-purple-400" title="6. Data Retention">
            <ul className="list-disc pl-6 space-y-1">
              <li><strong className="text-white">Active account data</strong> is retained for as long as your account exists.</li>
              <li><strong className="text-white">Marketplace listings and community posts</strong> are retained until you delete them or close your account.</li>
              <li><strong className="text-white">On account deletion</strong>, all personal data is permanently removed from our systems within 30 days. Community posts may be anonymised rather than deleted to preserve discussion context.</li>
              <li><strong className="text-white">Subscription records</strong> may be retained in Stripe's systems in accordance with their data retention policies.</li>
            </ul>
          </Section>

          {/* 7. POPIA & Governing Law */}
          <Section icon={Scale} iconColor="text-orange-400" title="7. Governing Law & POPIA">
            <p>
              This Privacy Policy is governed by the laws of the{' '}
              <strong className="text-white">Republic of South Africa</strong>, including the Protection of
              Personal Information Act 4 of 2013 (POPIA).
            </p>
            <p>
              CNC-Helpdesk is committed to processing personal information lawfully, fairly, and transparently,
              consistent with POPIA's conditions for lawful processing.
            </p>
            <p>
              If you believe your personal information has been processed in violation of POPIA, you have the
              right to lodge a complaint with the{' '}
              <strong className="text-white">Information Regulator of South Africa</strong> at{' '}
              <a href="https://inforegulator.org.za" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:underline">
                inforegulator.org.za
              </a>.
            </p>
          </Section>

          {/* 8. Changes */}
          <div className="bg-black/70 backdrop-blur-sm border border-zinc-800 rounded-lg p-8">
            <h2 className="text-xl font-bold text-white mb-4">8. Changes to This Policy</h2>
            <div className="text-zinc-300 space-y-3">
              <p>
                This Privacy Policy may be updated from time to time. The "Last updated" date at the top of this
                page will reflect any changes. Continued use of CNC-Helpdesk after changes are posted constitutes
                your acceptance of the revised policy.
              </p>
              <p>
                For significant changes that affect your rights, we will notify you via email or in-app notification
                where practical.
              </p>
            </div>
          </div>

          {/* Cross-link to Terms */}
          <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-white font-semibold mb-1">Terms of Service</p>
              <p className="text-zinc-400 text-sm">Beta disclaimer, AI content, warranties, governing law, and your responsibilities.</p>
            </div>
            <Link
              to="/terms"
              className="px-4 py-2 bg-orange-500/20 border border-orange-500/40 text-orange-400 hover:bg-orange-500/30 rounded text-sm font-medium transition-colors whitespace-nowrap"
            >
              View Terms of Service →
            </Link>
          </div>

        </div>

        <div className="h-12" />
      </div>
    </div>
  );
};

export default PrivacyPage;
